import type { LucideIcon } from 'lucide-react'
import { cn } from '@/utils'

const ACCENTS = {
  primary: 'text-primary',
  success: 'text-success',
  warning: 'text-warning-strong',
  destructive: 'text-destructive',
} as const

export interface StatCardProps {
  icon: LucideIcon
  label: string
  value: string
  sublabel?: string
  accent?: keyof typeof ACCENTS
  /** Optional call-to-action rendered as a text button below the value. */
  actionLabel?: string
  onAction?: () => void
  className?: string
}

/**
 * At-a-glance KPI tile for the business dashboard. Presentational — icon,
 * label, large value, optional sublabel, and an optional inline action.
 */
export function StatCard({
  icon: Icon,
  label,
  value,
  sublabel,
  accent = 'primary',
  actionLabel,
  onAction,
  className,
}: StatCardProps) {
  return (
    <div
      className={cn(
        'flex flex-col rounded-lg border border-border bg-card p-4',
        className,
      )}
    >
      <div className="mb-2 flex items-center gap-2">
        <Icon className={cn('h-4 w-4', ACCENTS[accent])} />
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      <p className="text-2xl font-bold text-foreground">{value}</p>
      {sublabel && <p className="mt-0.5 text-[11px] text-muted-foreground">{sublabel}</p>}
      {actionLabel && onAction && (
        <button
          type="button"
          onClick={onAction}
          className="mt-2 self-start text-xs font-medium text-primary hover:underline"
        >
          {actionLabel}
        </button>
      )}
    </div>
  )
}
